import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { concatMap, map, reduce } from 'rxjs/operators';
import { BulkChangeResult, BulkSelectionDataSource } from '@deighton/shared-ui/layout';
import { BulkRoleAssignmentResult } from './role-assignment.interface';
import { RoleAssignmentService } from './role-assignment.service';

/** Maximum number of user IDs per bulk mutation request (backend limit). */
const BATCH_SIZE: number = 200;

/** Maps the licensing bulk-mutation result onto the shared-ui {@link BulkChangeResult} shape. */
function toBulkChangeResult(result: BulkRoleAssignmentResult): BulkChangeResult {
  return {
    successCount: result.successCount,
    failures: result.failures.map(
      (failure: {
        userId: string;
        reason: string;
      }): {
        itemId: string;
        reason: string;
      } => ({ itemId: failure.userId, reason: failure.reason })
    )
  };
}

/** Splits an array into chunks of at most {@link BATCH_SIZE} items. */
function chunkArray<T>(arr: ReadonlyArray<T>, size: number): Array<Array<T>> {
  const chunks: Array<Array<T>> = [];
  for (let index: number = 0; index < arr.length; index += size) {
    chunks.push(arr.slice(index, index + size));
  }
  return chunks;
}

/** Aggregates multiple batch results into a single {@link BulkChangeResult}. */
function aggregateBulkResults(results: ReadonlyArray<BulkChangeResult>): BulkChangeResult {
  return results.reduce(
    (accumulated: BulkChangeResult, result: BulkChangeResult): BulkChangeResult => ({
      successCount: accumulated.successCount + result.successCount,
      failures: [...accumulated.failures, ...result.failures]
    }),
    { successCount: 0, failures: [] }
  );
}

/**
 * Adapts {@link RoleAssignmentService}'s GraphQL bulk endpoints to the shared-ui
 * {@link BulkSelectionDataSource} contract so `d-selectable-split-panel` can orchestrate
 * bulk license assign/revoke without any knowledge of GraphQL.
 */
@Injectable()
export class RoleBulkSelectionDataSource implements BulkSelectionDataSource {
  /** Underlying role mutation client. */
  private readonly roleAssignmentService: RoleAssignmentService = inject(RoleAssignmentService);

  /** Assigns a role (optionId) to the given users (itemIds), chunking when over 200 users. */
  public assign(optionId: string, itemIds: ReadonlyArray<string>): Observable<BulkChangeResult> {
    return this.executeBatched(itemIds, (batch: ReadonlyArray<string>): Observable<BulkChangeResult> =>
      this.roleAssignmentService.bulkAssignRoleToUsers(optionId, [...batch]).pipe(map(toBulkChangeResult))
    );
  }

  /** Revokes a role (optionId) from the given users (itemIds), chunking when over 200 users. */
  public revoke(optionId: string, itemIds: ReadonlyArray<string>): Observable<BulkChangeResult> {
    return this.executeBatched(itemIds, (batch: ReadonlyArray<string>): Observable<BulkChangeResult> =>
      this.roleAssignmentService.bulkRevokeRoleFromUsers(optionId, [...batch]).pipe(map(toBulkChangeResult))
    );
  }

  /** Runs a bulk operation in sequential batches when itemIds exceeds {@link BATCH_SIZE}. */
  private executeBatched(
    itemIds: ReadonlyArray<string>,
    operation: (batch: ReadonlyArray<string>) => Observable<BulkChangeResult>
  ): Observable<BulkChangeResult> {
    const batches: Array<Array<string>> = chunkArray(itemIds, BATCH_SIZE);
    if (batches.length === 0) {
      return from([{ successCount: 0, failures: [] }]);
    }
    if (batches.length === 1) {
      return operation(batches[0]);
    }
    return from(batches).pipe(
      concatMap((batch: Array<string>): Observable<BulkChangeResult> => operation(batch)),
      reduce(
        (accumulated: BulkChangeResult, result: BulkChangeResult): BulkChangeResult =>
          aggregateBulkResults([accumulated, result]),
        { successCount: 0, failures: [] }
      )
    );
  }
}
